import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartTypeEnum, DashboardFormsAnswersDetailItem, DashboardFormsQuestionIcon, QuestionTypeEnum } from '@core/interfaces';
import { endOfDay, mapIds, startOfDay } from '@core/utils/filters-utils';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty, isInteger } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { sentry } from 'sentry';
import { FilterService } from '../filter/filter.service';
import { FormsReportService } from '../services/forms-report.service';

@Component({
  selector: 'app-result',
  templateUrl: './dashboard-forms-detail.component.html',
  styleUrls: ['./dashboard-forms-detail.component.scss']
})
export class DashboardFormsDetailComponent implements OnInit, OnDestroy {
  public readonly questionIcons: DashboardFormsQuestionIcon[];
  private readonly removeSubscriptions: Subject<void>;

  public formsColors: string[];
  public isLoading: boolean;
  public questions: DashboardFormsAnswersDetailItem[];

  constructor(
    private fuseProgressBarService: FuseProgressBarService,
    private formsReportService: FormsReportService,
    private translateService: TranslateService,
    private filterService: FilterService,
    private route: ActivatedRoute,
    private titleService: Title,
    private router: Router,
  ) {
    this.isLoading = true;
    this.removeSubscriptions = new Subject<void>();
    this.formsColors = ['#1d4ed8', '#38bdf8', '#0f766e', '#34d399', '#a16207', '#fbbf24', '#44403c', '#a3a3a3', '#7e22ce', '#a78bfa'];
    this.questionIcons = [
      { type: 'multipleChoice', icon: 'radio_button_checked' },
      { type: 'list', icon: 'list_alt' },
      { type: 'numeric', icon: 'format_list_numbered' },
      { type: 'paragraph', icon: 'text_snippet' },
      { type: 'shortAnswer', icon: 'text_fields' },
      { type: 'checkboxes', icon: 'library_add_check' },
      { type: 'dropdown', icon: 'list_alt' },
      { type: 'date', icon: 'date_range' },
      { type: 'hour', icon: 'timer' },
      { type: 'multiplePicture', icon: 'collections' },
      { type: 'picture', icon: 'photo' },
      { type: 'currency', icon: 'attach_money' },
      { type: 'email', icon: 'mail' },
      { type: 'phone', icon: 'call' },
      { type: 'document', icon: 'assignment_ind' }
    ];
  }

  public ngOnInit(): void {
    if (isEmpty(this.route.snapshot.queryParams)) {
      this.router.navigate(['/relatorios/formularios']);
      return;
    }
    const formId = this.route.snapshot.queryParams.formId_eq;

    this.filterService.valueChanges.pipe(map(() => ({ source: 'filter' })))
      .pipe(
        takeUntil(this.removeSubscriptions),
        filter(() => this.filterService.filterForm.valid),
        debounceTime(200),
        switchMap(() => this.updateData()),
      ).subscribe();

    this.formsReportService.getFormById(formId)
      .then((selectedForm) => {
        this.filterService.selectedForm = selectedForm;
        const pageTitle = this.translateService.instant('DASHBOARD_FORMS.DETAIL.TITLE', { formName: selectedForm.name });
        this.titleService.setTitle(pageTitle);
      }).catch(() => {
        this.router.navigate(['/relatorios/formularios']);
      });

    this.filterService.setFilterFromQueryParams();
    this.filterService.reload();
  }

  public ngOnDestroy(): void {
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  private toQuery(value: any): any {
    return {
      formId_eq: this.route.snapshot.queryParams.formId_eq,
      locationId_in: mapIds(value.locations),
      segmentId_in: mapIds(value.segments),
      companyId_in: mapIds(value.companies),
      createdAt_lte: endOfDay(value.finalDate),
      createdAt_gte: startOfDay(value.initialDate),
      locationGroupId_in: mapIds(value.locationGroups),
      maintenanceTypeId_in: mapIds(value.maintenanceTypes),
      formAnswer_contains: value.formContent
    };
  }

  private async updateData(): Promise<void> {
    this.isLoading = true;
    this.fuseProgressBarService.show();

    const where = this.toQuery(this.filterService.value);

    try {
      this.questions = await this.formsReportService.accountFormsAnswersDetail(where);
      this.questions.map((question) => {
        question.answers.map((answer) => {
          answer['name'] = answer['value'];
          answer['value'] = answer['count'];

          return answer;
        });

        if (this.isGraph(question.type)) {
          question.chartType = ChartTypeEnum['bar'];
        }

        return question;
      });

      this.filterService.questionsCount = this.questions.length;
    } catch (err) {
      sentry.captureException(err);
    } finally {
      this.isLoading = false;
      this.fuseProgressBarService.hide();
    }
  }

  public isGraph(type: QuestionTypeEnum): boolean {
    return ['dropdown', 'checkboxes', 'multipleChoice'].includes(QuestionTypeEnum[type]);
  }

  public changeChart(question: DashboardFormsAnswersDetailItem, chartType: ChartTypeEnum): void {
    question.chartType = chartType;
  }

  public formatValue(value: number): number | string {
    return isInteger(value) ? value : '';
  }
}
